/** Company SSO (SAML): verify a domain, connect the identity provider, test, activate. Talks to the identity
 * service (apps/identity-service); see its README for what each step does in Entra External ID. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Globe, KeyRound, LogIn, Plus, RefreshCw, ShieldCheck, Trash2, Upload } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { ApiError, errorMessage } from "../api/client";
import {
  identity,
  identityEnabled,
  type ActivationPreview,
  type JoinRole,
  type MappedRole,
  type MappingClaim,
  type RoleMapping,
  type SsoConnection,
  type SsoDomain,
  type SsoOverview,
} from "../api/identity";
import { useOrg, useOrgId } from "../api/org";
import { Alert, Badge, Button, Card, CopyButton, Empty, Field, Modal, PageHead, Segmented, Select, Spinner, TextInput, useToast } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import type { MessageKey } from "../i18n/keys";
import { useDates } from "../lib/useDates";
import { roleName } from "./Team";

type Idp = "entra" | "okta" | "google";

const STATUS: Record<string, { tone: "ok" | "warn" | "danger" | "info"; label: MessageKey }> = {
  draft: { tone: "info", label: "team.sso.status.draft" },
  configured: { tone: "warn", label: "team.sso.status.configured" },
  tested: { tone: "info", label: "team.sso.status.tested" },
  active: { tone: "ok", label: "team.sso.status.active" },
  pending: { tone: "warn", label: "team.sso.status.pending" },
  verified: { tone: "ok", label: "team.sso.status.verified" },
  lost: { tone: "danger", label: "team.sso.status.lost" },
  pending_removal: { tone: "info", label: "team.sso.status.pendingRemoval" },
  pending_relink: { tone: "warn", label: "team.sso.status.pendingRelink" },
  relinked: { tone: "ok", label: "team.sso.status.relinked" },
  break_glass: { tone: "info", label: "team.sso.status.breakGlass" },
  failed: { tone: "danger", label: "team.sso.status.failed" },
};

function StatusBadge({ status }: { status: string }) {
  const t = useT();
  const s = STATUS[status];
  return <Badge tone={s?.tone ?? "info"}>{s ? t(s.label) : status}</Badge>;
}

const b = (chunks: ReactNode[]) => <b>{chunks}</b>;
const code = (chunks: ReactNode[]) => <code className="i18n-ltr">{chunks}</code>;

function Value({ label, value }: { label: string; value: string }) {
  return (
    <div className="field">
      <label>{label}</label>
      <div className="row" style={{ flexWrap: "nowrap" }}>
        <TextInput readOnly value={value} className="input mono" dir="ltr" onFocus={(e) => e.currentTarget.select()} aria-label={label} />
        <CopyButton text={value} />
      </div>
    </div>
  );
}

function Step({ n, title, done, children, description }: { n: number; title: string; done?: boolean; description?: ReactNode; children: ReactNode }) {
  return (
    <div className={`step${done ? " done" : ""}`} data-testid={`sso-step-${n}`}>
      <div className="step-num">{done ? <Check /> : n}</div>
      <div>
        <h3>{title}</h3>
        {description && <p className="muted small" style={{ marginTop: 4 }}>{description}</p>}
        <div className="step-body">{children}</div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------------- step 1: domains
function Domains({ orgId, overview }: { orgId: string; overview: SsoOverview }) {
  const t = useT();
  const dates = useDates();
  const qc = useQueryClient();
  const toast = useToast();
  const [domain, setDomain] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["sso", orgId] });
  const add = useMutation({
    mutationFn: () => identity.addDomain(orgId, domain),
    onSuccess: () => {
      setDomain("");
      refresh();
    },
  });
  const verify = useMutation({
    mutationFn: (d: SsoDomain) => identity.verifyDomain(orgId, d.id),
    onSuccess: (d) => {
      toast(
        d.status === "verified"
          ? t("team.sso.domains.verified", { domain: d.domain })
          : d.check?.error
            ? t("team.sso.domains.notFoundYet", { error: d.check.error })
            : t("team.sso.domains.noRecordYet"),
        d.status !== "verified",
      );
      refresh();
    },
    onError: (e) => {
      toast(errorMessage(e), true);
      refresh();
    },
  });
  const remove = useMutation({ mutationFn: (d: SsoDomain) => identity.removeDomain(orgId, d.id), onSuccess: refresh, onError: (e) => toast(errorMessage(e), true) });
  return (
    <>
      {overview.domains.map((d) => (
        <div key={d.id} className="card" style={{ padding: 14 }} data-testid={`domain-${d.domain}`}>
          <div className="row" style={{ justifyContent: "space-between" }}>
            <div className="row">
              <Globe size={16} />
              <b dir="ltr">{d.domain}</b>
              <StatusBadge status={d.status} />
            </div>
            <div className="row">
              {d.status !== "verified" && (
                <Button size="sm" variant="primary" icon={<RefreshCw />} loading={verify.isPending && verify.variables?.id === d.id} onClick={() => verify.mutate(d)}>
                  {t("team.sso.domains.verifyNow")}
                </Button>
              )}
              {overview.connection?.status !== "active" && (
                <Button size="sm" variant="ghost" icon={<Trash2 />} aria-label={t("team.sso.domains.remove", { domain: d.domain })} onClick={() => remove.mutate(d)} />
              )}
            </div>
          </div>
          {d.status !== "verified" ? (
            <div className="stack" style={{ marginTop: 12 }}>
              <p className="small muted">{t("team.sso.domains.addRecord")}</p>
              <div className="grid-2">
                <Value label={t("team.sso.dns.nameHost")} value={d.txt_name} />
                <Value label={t("team.sso.dns.value")} value={d.txt_value} />
              </div>
              {d.last_check_error && <p className="small faint">{t("team.sso.domains.lastCheck", { error: d.last_check_error })}</p>}
            </div>
          ) : (
            <p className="small faint" style={{ marginTop: 6 }}>
              {d.verified_at ? t("team.sso.domains.verifiedOn", { date: dates.date(d.verified_at) }) : t("team.sso.domains.verifiedKeep")}
            </p>
          )}
        </div>
      ))}
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          if (domain.trim()) add.mutate();
        }}
      >
        <TextInput placeholder={t("team.sso.domains.placeholder")} dir="ltr" value={domain} onChange={(e) => setDomain(e.target.value)} aria-label={t("team.sso.domains.label")} style={{ maxWidth: 280 }} />
        <Button type="submit" icon={<Plus />} loading={add.isPending}>
          {t("team.sso.domains.add")}
        </Button>
      </form>
      {add.error && <Alert tone="danger">{errorMessage(add.error)}</Alert>}
    </>
  );
}

// --------------------------------------------------------------------------- step 2: identity provider
const IDP_STEPS: Record<Idp, MessageKey[]> = {
  entra: ["team.sso.idp.entra.step1", "team.sso.idp.entra.step2", "team.sso.idp.entra.step3", "team.sso.idp.entra.step4", "team.sso.idp.entra.step5", "team.sso.idp.directFedStep"],
  okta: ["team.sso.idp.okta.step1", "team.sso.idp.okta.step2", "team.sso.idp.okta.step3", "team.sso.idp.okta.step4", "team.sso.idp.okta.step5", "team.sso.idp.directFedStep"],
  google: ["team.sso.idp.google.step1", "team.sso.idp.google.step2", "team.sso.idp.google.step3", "team.sso.idp.google.step4", "team.sso.idp.google.step5"],
};

function Instructions({ idp, sp }: { idp: Idp; sp: SsoOverview["service_provider"] }) {
  const email = sp.required_claims[0]?.name ?? "emailaddress";
  return (
    <ol className="small" style={{ margin: 0, paddingInlineStart: 18, display: "grid", gap: 6 }}>
      {IDP_STEPS[idp].map((key) => (
        <li key={key}>
          <FormattedMessage id={key} values={{ b, code, email }} />
        </li>
      ))}
    </ol>
  );
}

/** `<domain> TXT DirectFedAuthUrl=<sign-in URL>`: External ID federates a domain whose name the IdP's sign-in URL
 * does not share (acme.com signing in at acme.okta.com) only once the domain publishes this record. */
function DirectFedTxt({ orgId, overview, records }: { orgId: string; overview: SsoOverview; records: NonNullable<SsoConnection["direct_fed_txt"]> }) {
  const t = useT();
  const qc = useQueryClient();
  // Verifying a domain again also retries the federation, which is how External ID checks the record.
  const recheck = useMutation({
    mutationFn: () => Promise.all(overview.domains.filter((d) => records.some((r) => r.name === d.domain)).map((d) => identity.verifyDomain(orgId, d.id))),
    onSettled: () => qc.invalidateQueries({ queryKey: ["sso", orgId] }),
  });
  if (!records.length) return null;
  return (
    <Alert tone="warn" title={t("team.sso.directFed.title")}>
      <div className="stack" style={{ gap: 10, marginTop: 6 }} data-testid="direct-fed-txt">
        <span>{t("team.sso.directFed.body")}</span>
        {records.map((r) => (
          <div key={`${r.name}-${r.value}`} className="grid-2">
            <Value label={t("team.sso.dns.nameType", { type: r.type })} value={r.name} />
            <Value label={t("team.sso.dns.value")} value={r.value} />
          </div>
        ))}
        <div className="row">
          <Button size="sm" loading={recheck.isPending} onClick={() => recheck.mutate()}>
            {t("team.sso.directFed.checkAgain")}
          </Button>
        </div>
      </div>
    </Alert>
  );
}

function ConnectionSummary({ connection }: { connection: SsoConnection }) {
  const t = useT();
  const intl = useIntl();
  const dates = useDates();
  const inUse = connection.certificates.find((c) => c.in_use);
  return (
    <dl className="kv" data-testid="connection-summary">
      <dt>{t("team.sso.summary.issuer")}</dt>
      <dd className="mono ellipsis" dir="ltr">{connection.idp_entity_id}</dd>
      <dt>{t("team.sso.summary.signInUrl")}</dt>
      <dd className="mono ellipsis" dir="ltr">{connection.sso_url}</dd>
      <dt>{t("team.sso.summary.certificate")}</dt>
      <dd>
        {inUse ? t("team.sso.summary.certificateExpires", { subject: inUse.subject, date: dates.date(inUse.not_after) }) : "—"}{" "}
        {connection.certificate_expiring && <Badge tone="warn">{t("team.sso.summary.expiresSoon")}</Badge>}
      </dd>
      <dt>{t("team.sso.summary.metadata")}</dt>
      <dd>{connection.metadata_source === "url" ? t("team.sso.summary.metadataUrl", { date: dates.date(connection.metadata_refreshed_at) }) : t("team.sso.summary.metadataFile")}</dd>
      <dt>{t("team.sso.summary.externalId")}</dt>
      <dd>
        {connection.directory_federation_id
          ? t("team.sso.summary.federated", { domains: intl.formatList(connection.directory_domains, { type: "conjunction" }) })
          : t("team.sso.summary.waitingDomain")}
      </dd>
    </dl>
  );
}

/** Removes a connection that was never activated (draft, configured or tested): External ID drops its federation
 * and the company can start again. Nobody signs in through it yet, so nothing else changes. */
function RemoveConnection({ orgId }: { orgId: string }) {
  const t = useT();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const remove = useMutation({
    mutationFn: () => identity.deactivate(orgId),
    onSuccess: () => {
      setConfirm(false);
      qc.invalidateQueries({ queryKey: ["sso", orgId] });
    },
  });
  return (
    <>
      <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setConfirm(true)}>
        {t("team.sso.remove.button")}
      </Button>
      {confirm && (
        <Modal
          title={t("team.sso.remove.title")}
          onClose={() => setConfirm(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirm(false)}>
                {t("team.common.cancel")}
              </Button>
              <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
                {t("team.common.remove")}
              </Button>
            </>
          }
        >
          <p>{t("team.sso.remove.body")}</p>
          {remove.error && <Alert tone="danger">{errorMessage(remove.error)}</Alert>}
        </Modal>
      )}
    </>
  );
}

function IdentityProvider({ orgId, overview }: { orgId: string; overview: SsoOverview }) {
  const t = useT();
  const intl = useIntl();
  const qc = useQueryClient();
  const [idp, setIdp] = useState<Idp>("entra");
  const [mode, setMode] = useState<"url" | "file">("url");
  const [url, setUrl] = useState(overview.connection?.metadata_url ?? "");
  const [xml, setXml] = useState<{ name: string; text: string } | null>(null);
  const [editing, setEditing] = useState(!overview.connection || overview.connection.status === "draft");
  const save = useMutation({
    mutationFn: () => identity.saveMetadata(orgId, mode === "url" ? { metadata_url: url.trim() } : { metadata_xml: xml?.text ?? "" }),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["sso", orgId] });
    },
  });
  const sp = overview.service_provider;
  return (
    <>
      <div className="grid-2">
        <Value label={t("team.sso.idp.entityId")} value={sp.entity_id} />
        <Value label={t("team.sso.idp.replyUrl")} value={sp.reply_url} />
      </div>
      <p className="small muted">
        <FormattedMessage id="team.sso.idp.format" values={{ code, format: sp.name_id_format, claim: sp.required_claims[0]?.name ?? "" }} /> {sp.sign}.
      </p>
      <Segmented
        value={idp}
        onChange={setIdp}
        options={[
          { value: "entra", label: "Microsoft Entra ID" },
          { value: "okta", label: "Okta" },
          { value: "google", label: "Google Workspace" },
        ]}
      />
      <Instructions idp={idp} sp={sp} />
      {overview.connection && overview.connection.status !== "draft" && !editing ? (
        <div className="stack">
          <ConnectionSummary connection={overview.connection} />
          <DirectFedTxt orgId={orgId} overview={overview} records={overview.connection.direct_fed_txt ?? []} />
          {overview.connection.last_error && <Alert tone="danger" title={t("team.sso.idp.externalIdError")}>{overview.connection.last_error}</Alert>}
          {overview.connection.metadata_error && <Alert tone="warn" title={t("team.sso.idp.metadataError")}>{overview.connection.metadata_error}</Alert>}
          <div className="row">
            <Button size="sm" onClick={() => setEditing(true)}>
              {t("team.sso.idp.replace")}
            </Button>
            {overview.connection.status !== "active" && <RemoveConnection orgId={orgId} />}
          </div>
        </div>
      ) : (
        <div className="stack">
          <Segmented
            value={mode}
            onChange={setMode}
            options={[
              { value: "url", label: t("team.sso.idp.metadataUrl") },
              { value: "file", label: t("team.sso.idp.uploadXml") },
            ]}
          />
          {mode === "url" ? (
            <Field label={t("team.sso.idp.metadataUrl")} hint={t("team.sso.idp.metadataUrlHint")}>
              <TextInput value={url} dir="ltr" onChange={(e) => setUrl(e.target.value)} placeholder={t("team.sso.idp.metadataUrlPlaceholder")} aria-label={t("team.sso.idp.metadataUrl")} />
            </Field>
          ) : (
            <Field label={t("team.sso.idp.xmlFile")} hint={xml ? t("team.sso.idp.xmlChosen", { name: xml.name, count: xml.text.length, size: intl.formatNumber(xml.text.length) }) : t("team.sso.idp.xmlHint")}>
              <label className="btn" style={{ width: "fit-content" }}>
                <Upload size={16} /> {t("team.sso.idp.chooseFile")}
                <input
                  type="file"
                  accept=".xml,application/xml,text/xml"
                  hidden
                  aria-label={t("team.sso.idp.xmlFile")}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) setXml({ name: file.name, text: await file.text() });
                  }}
                />
              </label>
            </Field>
          )}
          {save.error && <Alert tone="danger">{errorMessage(save.error)}</Alert>}
          <div className="row">
            <Button variant="primary" icon={<KeyRound />} loading={save.isPending} disabled={mode === "url" ? !url.trim() : !xml} onClick={() => save.mutate()}>
              {t("team.sso.idp.save")}
            </Button>
            {overview.connection && overview.connection.status !== "draft" && (
              <Button variant="ghost" onClick={() => setEditing(false)}>
                {t("team.common.cancel")}
              </Button>
            )}
            {overview.connection && overview.connection.status === "draft" && <RemoveConnection orgId={orgId} />}
          </div>
        </div>
      )}
    </>
  );
}

// ----------------------------------------------------------------------------- who joins, with which role
const CLAIM_LABEL: Record<MappingClaim, MessageKey> = { groups: "team.sso.members.claim.groups", roles: "team.sso.members.claim.roles" };

function NewMembers({ orgId, overview, connection }: { orgId: string; overview: SsoOverview; connection: SsoConnection }) {
  const t = useT();
  const qc = useQueryClient();
  const toast = useToast();
  const [role, setRole] = useState<JoinRole>(connection.default_role);
  const [rows, setRows] = useState<RoleMapping[]>(connection.role_mappings);
  useEffect(() => {
    setRole(connection.default_role);
    setRows(connection.role_mappings);
  }, [connection.default_role, connection.role_mappings]);
  const mapping = overview.roles.mapping_enabled;
  const save = useMutation({
    mutationFn: () =>
      identity.putRoles(orgId, {
        default_role: role,
        ...(mapping ? { mappings: rows.filter((r) => r.value.trim()).map((r) => ({ claim: r.claim, value: r.value.trim(), role: r.role })) } : {}),
      }),
    onSuccess: () => {
      toast(t("team.common.saved"));
      qc.invalidateQueries({ queryKey: ["sso", orgId] });
    },
  });
  const changed =
    role !== connection.default_role ||
    (mapping && JSON.stringify(rows.map((r) => [r.claim, r.value, r.role])) !== JSON.stringify(connection.role_mappings.map((r) => [r.claim, r.value, r.role])));
  const update = (i: number, patch: Partial<RoleMapping>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <Card title={t("team.sso.members.title")} description={t("team.sso.members.description")}>
      <div className="stack" data-testid="new-members">
        <Field label={t("team.sso.members.defaultRole")} hint={t("team.sso.members.defaultRoleHint")}>
          <div style={{ maxWidth: 240 }}>
            <Select<JoinRole> ariaLabel={t("team.sso.members.defaultRole")} value={role} onChange={setRole} options={overview.roles.default_choices.map((r) => ({ value: r, label: roleName(t, r) }))} />
          </div>
        </Field>
        {mapping && (
          <div className="stack" data-testid="role-mappings">
            <div>
              <div className="small" style={{ fontWeight: 600 }}>{t("team.sso.members.mappingTitle")}</div>
              <p className="small muted" style={{ marginTop: 2 }}>{t("team.sso.members.mappingHelp")}</p>
            </div>
            {rows.length > 0 && (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t("team.sso.members.col.claim")}</th>
                      <th>{t("team.sso.members.col.value")}</th>
                      <th>{t("team.sso.members.col.role")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td style={{ width: 150 }}>
                          <Select<MappingClaim> ariaLabel={t("team.sso.members.claimN", { n: i + 1 })} value={r.claim} onChange={(claim) => update(i, { claim })} options={overview.roles.mapping_claims.map((c) => ({ value: c, label: t(CLAIM_LABEL[c]) }))} />
                        </td>
                        <td>
                          <TextInput aria-label={t("team.sso.members.valueN", { n: i + 1 })} dir="auto" value={r.value} onChange={(e) => update(i, { value: e.target.value })} placeholder={t("team.sso.members.valuePlaceholder")} />
                        </td>
                        <td style={{ width: 150 }}>
                          <Select<MappedRole> ariaLabel={t("team.sso.members.roleN", { n: i + 1 })} value={r.role} onChange={(mapped) => update(i, { role: mapped })} options={overview.roles.mapping_choices.map((c) => ({ value: c, label: roleName(t, c) }))} />
                        </td>
                        <td style={{ width: 48, textAlign: "end" }}>
                          <Button size="sm" variant="ghost" icon={<Trash2 />} aria-label={t("team.sso.members.removeN", { n: i + 1 })} onClick={() => setRows(rows.filter((_, j) => j !== i))} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div>
              <Button size="sm" icon={<Plus />} onClick={() => setRows([...rows, { claim: "groups", value: "", role: "viewer" }])}>
                {t("team.sso.members.addMapping")}
              </Button>
            </div>
          </div>
        )}
        {save.error && <Alert tone="danger">{errorMessage(save.error)}</Alert>}
        <div>
          <Button variant="primary" disabled={!changed} loading={save.isPending} onClick={() => save.mutate()}>
            {t("team.sso.members.save")}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ------------------------------------------------------------------------------------------- step 3: test
function TestSignIn({ orgId, overview }: { orgId: string; overview: SsoOverview }) {
  const t = useT();
  const intl = useIntl();
  const dates = useDates();
  // never the admin's own address: someone with a Solar OS sign-in cannot test (External ID sends them to it)
  const [testEmail, setTestEmail] = useState("");
  const start = useMutation({
    mutationFn: () => identity.startTest(orgId, testEmail, `${window.location.origin}/sso`),
    onSuccess: (r) => window.location.assign(r.authorize_url),
  });
  const connection = overview.connection;
  const ready = !!connection?.directory_federation_id;
  const last = connection?.last_test;
  return (
    <>
      {!ready && <p className="small muted">{t("team.sso.test.notReady")}</p>}
      {last && last.status !== "started" && (
        <Alert tone={last.status === "passed" ? "ok" : "danger"} title={last.status === "passed" ? t("team.sso.test.passedTitle") : t("team.sso.test.failedTitle")}>
          {last.status === "passed" ? t("team.sso.test.passedBody", { email: last.email, date: dates.date(last.completed_at) }) : last.error}
        </Alert>
      )}
      <p className="small muted" data-testid="sso-tester-hint">
        {connection?.directory_domains.length ? (
          <FormattedMessage id="team.sso.test.hintDomains" values={{ b, domains: intl.formatList(connection.directory_domains, { type: "disjunction" }) }} />
        ) : (
          <FormattedMessage id="team.sso.test.hint" values={{ b }} />
        )}
      </p>
      <div className="row">
        <TextInput
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          aria-label={t("team.sso.test.email")}
          dir="auto"
          placeholder={`colleague@${connection?.directory_domains[0] ?? "yourcompany.com"}`}
          style={{ maxWidth: 280 }}
          disabled={!ready}
        />
        <Button variant={connection?.status === "configured" ? "primary" : "default"} icon={<DirIcon><LogIn /></DirIcon>} disabled={!ready || !testEmail.includes("@")} loading={start.isPending} onClick={() => start.mutate()}>
          {t("team.sso.test.start")}
        </Button>
      </div>
      {start.error &&
        (start.error instanceof ApiError && (start.error.problem as { code?: string }).code === "tester_has_other_signin" ? (
          <Alert tone="warn" title={t("team.sso.test.otherTesterTitle")} data-testid="tester-has-other-signin">
            {t("team.sso.test.otherTester", { email: testEmail })}
          </Alert>
        ) : (
          <Alert tone="danger">{errorMessage(start.error)}</Alert>
        ))}
      <p className="small faint">
        {overview.test_flow_separate ? t("team.sso.test.separateFlow") : t("team.sso.test.normalFlow")}
      </p>
    </>
  );
}

// --------------------------------------------------------------------------------------- step 4: activate
function PeopleTable({ title, people, children }: { title: string; people: { email: string; role: string }[]; children?: (email: string) => ReactNode }) {
  const t = useT();
  if (!people.length) return null;
  return (
    <div>
      <div className="small muted" style={{ marginBottom: 6, fontWeight: 600 }}>
        {t("team.sso.activate.peopleTitle", { title, count: people.length })}
      </div>
      <div className="table-wrap">
        <table className="table">
          <tbody>
            {people.map((p) => (
              <tr key={p.email}>
                <td className="cell-main" dir="auto">{p.email}</td>
                <td className="muted">{roleName(t, p.role)}</td>
                {children && <td style={{ textAlign: "end" }}>{children(p.email)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Activate({ orgId, overview }: { orgId: string; overview: SsoOverview }) {
  const t = useT();
  const intl = useIntl();
  const qc = useQueryClient();
  const toast = useToast();
  const [breakGlass, setBreakGlass] = useState<string[] | null>(null);
  const [confirm, setConfirm] = useState(false);
  const ready = overview.connection?.status === "tested";
  const preview = useQuery({
    queryKey: ["sso-preview", orgId, breakGlass?.join(",")],
    queryFn: () => identity.preview(orgId, breakGlass ?? undefined),
    enabled: !!overview.connection?.directory_federation_id,
  });
  useEffect(() => {
    if (preview.data && breakGlass === null) setBreakGlass(preview.data.break_glass.map((p) => p.email));
  }, [preview.data, breakGlass]);
  const activate = useMutation({
    mutationFn: () => identity.activate(orgId, breakGlass ?? []),
    onSuccess: () => {
      setConfirm(false);
      toast(t("team.sso.activate.done"));
      qc.invalidateQueries({ queryKey: ["sso", orgId] });
      qc.invalidateQueries({ queryKey: ["sso-migrations", orgId] });
    },
  });
  const data: ActivationPreview | undefined = preview.data;
  const owners = useMemo(() => (data ? [...data.moving, ...data.break_glass].filter((p) => p.role === "owner") : []), [data]);
  if (!overview.connection?.directory_federation_id) return <p className="small muted">{t("team.sso.activate.afterTest")}</p>;
  if (preview.isLoading || !data) return <Spinner label={t("team.common.loading")} />;
  const toggle = (email: string) => setBreakGlass((list) => ((list ?? []).includes(email) ? (list ?? []).filter((e) => e !== email) : [...(list ?? []), email]));
  return (
    <>
      <p className="small muted">
        <FormattedMessage id="team.sso.activate.explain" values={{ b, domains: intl.formatList(data.domains, { type: "conjunction" }) }} />
      </p>
      <PeopleTable title={t("team.sso.activate.moving")} people={data.moving.filter((p) => p.role !== "owner")} />
      <PeopleTable title={t("team.sso.activate.owners")} people={owners}>
        {(email) => (
          <label className="row small" style={{ justifyContent: "flex-end" }}>
            <input type="checkbox" checked={(breakGlass ?? []).includes(email)} onChange={() => toggle(email)} aria-label={t("team.sso.activate.keepLabel", { email })} /> {t("team.sso.activate.keep")}
          </label>
        )}
      </PeopleTable>
      <PeopleTable title={t("team.sso.activate.guests")} people={data.guests} />
      {data.problems.map((p) => (
        <Alert key={p} tone="warn">
          {p}
        </Alert>
      ))}
      <div>
        <Button variant="primary" icon={<ShieldCheck />} disabled={!ready || data.problems.length > 0} onClick={() => setConfirm(true)}>
          {t("team.sso.activate.button")}
        </Button>
      </div>
      {confirm && (
        <Modal
          title={t("team.sso.activate.confirmTitle")}
          onClose={() => setConfirm(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirm(false)}>
                {t("team.common.cancel")}
              </Button>
              <Button variant="primary" loading={activate.isPending} onClick={() => activate.mutate()}>
                {t("team.sso.activate.confirm")}
              </Button>
            </>
          }
        >
          <div className="stack">
            <p>{t("team.sso.activate.confirmBody", { moving: data.moving.length, kept: (breakGlass ?? []).length })}</p>
            {activate.error && <Alert tone="danger">{errorMessage(activate.error)}</Alert>}
          </div>
        </Modal>
      )}
    </>
  );
}

// ------------------------------------------------------------------------------------------ when active
function ActiveStatus({ orgId, overview }: { orgId: string; overview: SsoOverview }) {
  const t = useT();
  const intl = useIntl();
  const dates = useDates();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const migrations = useQuery({ queryKey: ["sso-migrations", orgId], queryFn: () => identity.migrations(orgId), refetchInterval: 15_000 });
  const off = useMutation({
    mutationFn: () => identity.deactivate(orgId),
    onSuccess: () => {
      setConfirm(false);
      qc.invalidateQueries({ queryKey: ["sso", orgId] });
    },
  });
  const c = overview.connection!;
  return (
    <Card
      title={t("team.sso.active.title")}
      description={t("team.sso.active.description", { domains: intl.formatList(c.directory_domains, { type: "conjunction" }), date: dates.date(c.activated_at) })}
      actions={
        <Button size="sm" variant="danger" onClick={() => setConfirm(true)}>
          {t("team.sso.active.turnOff")}
        </Button>
      }
      pad={false}
    >
      {migrations.isLoading ? (
        <Spinner label={t("team.common.loading")} />
      ) : (
        <div className="table-wrap">
          <table className="table" data-testid="migrations">
            <thead>
              <tr>
                <th>{t("team.sso.active.col.member")}</th>
                <th>{t("team.sso.active.col.status")}</th>
                <th className="hide-sm">{t("team.sso.active.col.detail")}</th>
              </tr>
            </thead>
            <tbody>
              {migrations.data?.items.map((m) => (
                <tr key={m.id}>
                  <td className="cell-main" dir="auto">{m.email}</td>
                  <td>
                    <StatusBadge status={m.state} />
                  </td>
                  <td className="hide-sm small muted">{m.state === "relinked" ? t("team.sso.active.since", { date: dates.date(m.relinked_at) }) : m.state === "pending_relink" && m.detail?.startsWith("waiting") ? "" : m.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {confirm && (
        <Modal
          title={t("team.sso.active.confirmTitle")}
          onClose={() => setConfirm(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirm(false)}>
                {t("team.common.cancel")}
              </Button>
              <Button variant="danger" loading={off.isPending} onClick={() => off.mutate()}>
                {t("team.sso.active.confirm")}
              </Button>
            </>
          }
        >
          <p>{t("team.sso.active.confirmBody")}</p>
          {off.error && <Alert tone="danger">{errorMessage(off.error)}</Alert>}
        </Modal>
      )}
    </Card>
  );
}

function AuditLog({ orgId }: { orgId: string }) {
  const t = useT();
  const dates = useDates();
  const audit = useQuery({ queryKey: ["sso-audit", orgId], queryFn: () => identity.audit(orgId) });
  if (!audit.data?.items.length) return null;
  return (
    <Card title={t("team.sso.activity.title")} description={t("team.sso.activity.description")} pad={false}>
      <div className="table-wrap">
        <table className="table">
          <tbody>
            {audit.data.items.map((e) => (
              <tr key={e.id}>
                <td className="small muted" style={{ whiteSpace: "nowrap" }}>
                  {dates.date(e.created_at)}
                </td>
                <td className="mono small" dir="ltr">{e.action}</td>
                <td className="small ellipsis" dir="auto">{e.actor === "system" ? "Solar OS" : e.actor}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function Sso() {
  const t = useT();
  const dates = useDates();
  const intl = useIntl();
  const orgId = useOrgId();
  const { can } = useOrg();
  const canManage = can("team", "edit");
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  const qc = useQueryClient();
  const overview = useQuery({ queryKey: ["sso", orgId], queryFn: () => identity.overview(orgId), enabled: identityEnabled() && canManage });
  useEffect(() => {
    const result = params.get("test");
    if (!result) return;
    toast(result === "passed" ? t("team.sso.toast.testPassed") : t("team.sso.toast.testFailed"), result !== "passed");
    qc.invalidateQueries({ queryKey: ["sso", orgId] });
    params.delete("test");
    setParams(params, { replace: true });
  }, [params, setParams, toast, qc, orgId, t]);

  const head = (
    <PageHead
      title={t("team.sso.page.title")}
      description={t("team.sso.page.description")}
      actions={overview.data?.connection && <StatusBadge status={overview.data.connection.status} />}
    />
  );
  if (!identityEnabled())
    return (
      <>
        {head}
        <Empty icon={<ShieldCheck />} title={t("team.sso.page.unavailable")} />
      </>
    );
  if (!canManage)
    return (
      <>
        {head}
        <Alert tone="info">{t("team.sso.page.adminsOnly")}</Alert>
      </>
    );
  if (overview.isLoading) return <Spinner label={t("team.common.loading")} />;
  if (overview.error || !overview.data)
    return (
      <>
        {head}
        <Alert tone="danger">{errorMessage(overview.error)}</Alert>
      </>
    );
  const o = overview.data;
  const c = o.connection;
  const verified = o.domains.some((d) => d.status === "verified" || d.status === "lost");
  const lost = o.domains.filter((d) => d.status === "lost");
  return (
    <>
      {head}
      <div className="stack">
        {lost.length > 0 && (
          <Alert tone="danger" title={t("team.sso.lost.title")}>
            {t("team.sso.lost.body", { count: lost.length, domains: intl.formatList(lost.map((d) => d.domain), { type: "conjunction" }) })}
          </Alert>
        )}
        {c?.certificate_expiring && (
          <Alert tone="warn" title={t("team.sso.cert.title")}>
            {t("team.sso.cert.body", { date: dates.date(c.certificate_expires_at) })}{" "}
            {c.metadata_source === "url" ? t("team.sso.cert.fromUrl") : t("team.sso.cert.upload")}
          </Alert>
        )}
        {c?.status === "active" && <ActiveStatus orgId={orgId} overview={o} />}
        <Card>
          <div className="steps">
            <Step n={1} title={t("team.sso.step1.title")} done={verified} description={t("team.sso.step1.description")}>
              <Domains orgId={orgId} overview={o} />
            </Step>
            <Step n={2} title={t("team.sso.step2.title")} done={!!c && c.status !== "draft"} description={t("team.sso.step2.description")}>
              <IdentityProvider orgId={orgId} overview={o} />
            </Step>
            <Step n={3} title={t("team.sso.step3.title")} done={!!c?.tested_at} description={t("team.sso.step3.description")}>
              <TestSignIn orgId={orgId} overview={o} />
            </Step>
            {c?.status !== "active" && (
              <Step n={4} title={t("team.sso.step4.title")} description={t("team.sso.step4.description")}>
                <Activate orgId={orgId} overview={o} />
              </Step>
            )}
          </div>
        </Card>
        {c && c.status !== "draft" && <NewMembers orgId={orgId} overview={o} connection={c} />}
        <AuditLog orgId={orgId} />
      </div>
    </>
  );
}
